import type { JSONContent } from '@tiptap/core';
import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError, type PagedData } from './common';
import type { RunnerType } from './daemon';

/** 다중 대상 입력 1건. 러너와 그 러너에 바인딩된 에이전트 쌍이며 내 러너만 허용된다. */
export type RunnerScheduleTargetInput = {
  daemonId: string;
  agentConfigId: string;
};

/** 저장된 대상 행. `sortOrder`는 서버가 입력 순서대로 매긴다. */
export type RunnerScheduleTarget = RunnerScheduleTargetInput & {
  id: string;
  sortOrder: number;
};

/** 다중 대상 저장 400 응답이 알려주는 거절된 러너. `code`는 러너별 거절 사유다. */
export type RunnerScheduleTargetFailure = {
  daemonId: string;
  code: string;
};

export type RunnerScheduleInput = {
  title: string;
  prompt: JSONContent;
  frequency: 'DAILY' | 'WEEKLY';
  weekday?: number | null;
  hour: number;
  minute: number;
  timezone: string;
  isEnabled?: boolean;
  agentConfigId: string;
  targetDaemonId?: string | null;
  targets?: RunnerScheduleTargetInput[];
  runnerType: RunnerType;
  model?: string | null;
  fastMode?: boolean;
  planMode?: boolean;
  effort?: string | null;
  idleTimeoutMs?: number | null;
  useWorktree?: boolean;
  repositoryId?: string | null;
  baseBranch?: string | null;
  userConventionIds?: string[];
};

export type RunnerSchedule = Omit<RunnerScheduleInput, 'targets'> & {
  targets: RunnerScheduleTarget[];
  id: string;
  projectId: string;
  createdByMemberId: string;
  isEnabled: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type RunnerScheduleRun = {
  id: string;
  scheduleId: string;
  scheduledFor: string;
  status: 'DISPATCHED' | 'SKIPPED' | 'FAILED';
  reason: string | null;
  daemonTriggerId: string | null;
  // 이 런이 어느 러너 앞으로 발행됐는지. 다중 대상 도입 전 레거시 행은 null이다.
  targetDaemonId: string | null;
  createdAt: string;
};

/**
 * 다중 대상 저장 400 응답의 실패 러너 목록을 읽는다.
 * 공통 `parseApiError`는 본문을 소비하므로 사본에서 먼저 꺼낸다.
 */
const readTargetFailures = async (response: Response): Promise<RunnerScheduleTargetFailure[] | undefined> => {
  try {
    const body = (await response.clone().json()) as { targetFailures?: unknown };
    if (!Array.isArray(body.targetFailures)) return undefined;
    const failures = body.targetFailures.filter(
      (item): item is RunnerScheduleTargetFailure =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as RunnerScheduleTargetFailure).daemonId === 'string' &&
        typeof (item as RunnerScheduleTargetFailure).code === 'string',
    );
    return failures.length > 0 ? failures : undefined;
  } catch {
    return undefined;
  }
};

/** 저장 실패 오류에 실린 러너별 거절 사유. 없으면 빈 목록이다. */
export const getRunnerScheduleTargetFailures = (error: unknown): RunnerScheduleTargetFailure[] => {
  const failures = (error as { targetFailures?: unknown } | null)?.targetFailures;
  return Array.isArray(failures) ? (failures as RunnerScheduleTargetFailure[]) : [];
};

const request = async <T>(token: string, projectId: string, suffix = '', init?: RequestInit): Promise<T> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/runner-schedules${suffix}`, {
    cache: 'no-store',
    ...init,
    headers: authHeaders(token, Boolean(init?.body)),
  });
  if (!response.ok) {
    const failures = await readTargetFailures(response);
    const error = await parseApiError(response, `Runner schedule request failed: ${response.status}`);
    if (failures) Object.assign(error, { targetFailures: failures });
    throw error;
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
};

export const fetchRunnerSchedules = (token: string, projectId: string, page = 1) =>
  request<PagedData<RunnerSchedule>>(token, projectId, `?page=${page}&pageSize=20`);

export const fetchRunnerSchedule = async (token: string, projectId: string, id: string) =>
  (await request<{ data: RunnerSchedule }>(token, projectId, `/${id}`)).data;

export const fetchRunnerScheduleRuns = (token: string, projectId: string, id: string, page = 1, pageSize = 10) =>
  request<PagedData<RunnerScheduleRun>>(token, projectId, `/${id}/runs?page=${page}&pageSize=${pageSize}`);

export const createRunnerSchedule = async (token: string, projectId: string, input: RunnerScheduleInput) =>
  (await request<{ data: RunnerSchedule }>(token, projectId, '', { method: 'POST', body: JSON.stringify(input) })).data;

export const updateRunnerSchedule = async (
  token: string,
  projectId: string,
  id: string,
  input: Partial<RunnerScheduleInput>,
) =>
  (
    await request<{ data: RunnerSchedule }>(token, projectId, `/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    })
  ).data;

export const setRunnerScheduleEnabled = async (token: string, projectId: string, id: string, isEnabled: boolean) =>
  (
    await request<{ data: RunnerSchedule }>(token, projectId, `/${id}/enabled`, {
      method: 'PATCH',
      body: JSON.stringify({ isEnabled }),
    })
  ).data;

export const deleteRunnerSchedule = (token: string, projectId: string, id: string) =>
  request<void>(token, projectId, `/${id}`, { method: 'DELETE' });

// 대상 러너 수만큼 런이 생기므로 배열로 돌려준다.
export const runRunnerSchedule = async (token: string, projectId: string, id: string) =>
  (await request<{ data: RunnerScheduleRun[] }>(token, projectId, `/${id}/run`, { method: 'POST' })).data;
